/* PortraitCropModal — drag-to-position + zoom for any portrait upload.
 * Reusable across IrisChat, ExpertsHub, PTPage.
 *
 * Props:
 *   file:        File (the selected image)
 *   onCancel:    () => void
 *   onConfirm:   (Blob) => Promise<void>   // receives 512x512 JPEG blob
 *   title?:      string (header text)
 *   accent?:     tailwind color class for accents (default rose)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Move, ZoomIn, Check, X as XIcon } from 'lucide-react';

const OUTPUT_SIZE = 512;   // final exported square px

export default function PortraitCropModal({ file, onCancel, onConfirm, title = 'Beskär bilden', accent = 'rose' }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);         // current zoom factor (over base-fit scale)
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // translation from center, in canvas px
  const [busy, setBusy] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0, pointers: new Map() });
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  // Load file into image URL + natural size
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    const i = new Image();
    i.onload = () => {
      setNaturalSize({ w: i.naturalWidth, h: i.naturalHeight });
    };
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Compute base-fit scale so the image COVERS the square crop area
  const baseFitScale = (() => {
    if (!naturalSize.w || !naturalSize.h) return 1;
    return Math.max(OUTPUT_SIZE / naturalSize.w, OUTPUT_SIZE / naturalSize.h);
  })();
  const effScale = baseFitScale * scale;
  // Image displayed size in the preview (px). Preview is OUTPUT_SIZE display px.
  const dispW = naturalSize.w * effScale;
  const dispH = naturalSize.h * effScale;

  // Constrain offset so the image always covers the crop square
  const constrain = useCallback((x, y) => {
    const maxX = Math.max(0, (dispW - OUTPUT_SIZE) / 2);
    const maxY = Math.max(0, (dispH - OUTPUT_SIZE) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, [dispW, dispH]);

  useEffect(() => { setOffset((o) => constrain(o.x, o.y)); }, [constrain]);

  const onPointerDown = (e) => {
    if (!containerRef.current) return;
    containerRef.current.setPointerCapture?.(e.pointerId);
    dragRef.current.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (dragRef.current.pointers.size === 1) {
      dragRef.current.active = true;
      dragRef.current.startX = e.clientX;
      dragRef.current.startY = e.clientY;
      dragRef.current.baseX = offset.x;
      dragRef.current.baseY = offset.y;
    } else if (dragRef.current.pointers.size === 2) {
      // pinch start
      const pts = Array.from(dragRef.current.pointers.values());
      dragRef.current.pinchStart = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      dragRef.current.pinchScale = scale;
      dragRef.current.active = false;
    }
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.pointers.has(e.pointerId)) return;
    dragRef.current.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (dragRef.current.pointers.size === 2 && dragRef.current.pinchStart) {
      const pts = Array.from(dragRef.current.pointers.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const next = Math.min(4, Math.max(1, dragRef.current.pinchScale * (dist / dragRef.current.pinchStart)));
      setScale(next);
      return;
    }
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(constrain(dragRef.current.baseX + dx, dragRef.current.baseY + dy));
  };

  const onPointerUp = (e) => {
    dragRef.current.pointers.delete(e.pointerId);
    if (dragRef.current.pointers.size < 2) dragRef.current.pinchStart = null;
    if (dragRef.current.pointers.size === 0) dragRef.current.active = false;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    setScale((s) => Math.min(4, Math.max(1, s + delta)));
  };

  const save = async () => {
    if (!imgRef.current || !naturalSize.w) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      // The crop square is centered on the preview. In canvas px,
      // the image is drawn at (cx - dispW/2 + offset.x, cy - dispH/2 + offset.y)
      // where cx, cy = OUTPUT_SIZE/2.
      const cx = OUTPUT_SIZE / 2;
      const cy = OUTPUT_SIZE / 2;
      const drawX = cx - dispW / 2 + offset.x;
      const drawY = cy - dispH / 2 + offset.y;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      ctx.drawImage(imgRef.current, drawX, drawY, dispW, dispH);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) throw new Error('Kunde inte exportera bild');
      await onConfirm(blob);
    } catch (e) {
      // surfaced by caller via toast typically
      console.warn('crop save failed', e);
    } finally {
      setBusy(false);
    }
  };

  const ACCENT = {
    rose:  { bg: 'bg-rose-500 hover:bg-rose-600',   border: 'border-rose-300',  text: 'text-rose-700',  head: 'from-rose-500 to-pink-500' },
    amber: { bg: 'bg-amber-500 hover:bg-amber-600', border: 'border-amber-300', text: 'text-amber-700', head: 'from-amber-500 to-orange-500' },
    sky:   { bg: 'bg-sky-500 hover:bg-sky-600',     border: 'border-sky-300',   text: 'text-sky-700',   head: 'from-sky-500 to-cyan-500' },
    violet:{ bg: 'bg-violet-500 hover:bg-violet-600', border: 'border-violet-300', text: 'text-violet-700', head: 'from-violet-500 to-fuchsia-500' },
  };
  const acc = ACCENT[accent] || ACCENT.rose;

  return (
    <div className="fixed inset-0 z-[140] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={busy ? undefined : onCancel}>
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`px-4 py-3 flex justify-between items-center text-white bg-gradient-to-r ${acc.head}`}>
          <h3 className="font-extrabold text-sm flex items-center gap-1.5"><Move className="w-4 h-4" /> {title}</h3>
          <button onClick={onCancel} disabled={busy} data-testid="crop-cancel"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className={`text-[11px] ${acc.text} italic`}>
            Dra för att flytta, snurra hjulet eller dra slidern för att zooma. Pincha på mobil.
          </div>
          <div
            ref={containerRef}
            className="relative mx-auto rounded-2xl overflow-hidden bg-gray-900 select-none touch-none cursor-move"
            style={{ width: OUTPUT_SIZE / 1.5, height: OUTPUT_SIZE / 1.5 }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            data-testid="crop-canvas"
          >
            {imgUrl && (
              <img
                ref={imgRef}
                src={imgUrl}
                alt="crop"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: `${dispW / 1.5}px`,
                  height: `${dispH / 1.5}px`,
                  transform: `translate(calc(-50% + ${offset.x / 1.5}px), calc(-50% + ${offset.y / 1.5}px))`,
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
              />
            )}
            <div className="absolute inset-0 pointer-events-none border-2 border-white/80 rounded-2xl" />
          </div>
          <div>
            <label className={`text-[11px] font-bold ${acc.text} flex items-center gap-1`}><ZoomIn className="w-3 h-3" /> Zoom</label>
            <input
              type="range"
              min="1"
              max="4"
              step="0.05"
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              data-testid="crop-zoom"
              className="w-full accent-rose-500"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} disabled={busy} data-testid="crop-cancel-btn" className={`flex-1 py-2 rounded-xl bg-white border-2 ${acc.border} ${acc.text} text-xs font-bold disabled:opacity-50`}>Avbryt</button>
            <button onClick={save} disabled={busy || !naturalSize.w} data-testid="crop-confirm" className={`flex-1 py-2 rounded-xl ${acc.bg} text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1.5`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {busy ? 'Sparar…' : 'Spara porträtt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
